<template>
  <div class="bag-hero">
    <div class="row">
      <div class="col-12">
        <div v-if="!store">
          <h1 class="bag-hero__bag-name">
            {{ bag.name }}
          </h1>
          <hr>
        </div>
        <div
          class="bag-hero__logo-container"
          :style="{padding: store && store.storeProfile === 0 ? '0 1rem' : '0 0.5rem'}"
        >
          <svgicon
            :name="!store ? 'Illustrative/Location' : 'Logos/' + translateStoreType(store.storeProfile)"
            role="presentation"
            :original="true"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <store-info
          v-if="store"
          class="bag-hero__store-info"
          @changeStore="showStoreSearchModal"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="bag-hero__bag-info">
          <div class="bag-hero__bag-info--column">
            <div
              class="col bag-hero__hero-price-container"
              :class="offer ? '' : 'd-none'"
            >
              <grocery-bag-price-splash
                :color="color"
                :offer="offer"
                :current-price="currentPrice"
                :current-price-recommended="currentPriceRecommended"
              />
            </div>
          </div>
          <div class="bag-hero__bag-info--column">
            <div
              class="bag-hero__order-options"
            >
              <div class="bag-hero__order-options-items">
                <div
                  v-if="peoples"
                  class="bag-hero__order-options-items--peoples"
                >
                  <p class="bag-hero__order-options-label">
                    Portioner
                  </p>
                  <p class="bag-hero__radio-wrapper">
                    <template
                      v-for="person in peoples"
                    >
                      <input
                        :id="bag.name + '-hero-' + person"
                        :key="'input' + bag.name + '-hero-' + person"
                        v-model="selectedPersons"
                        :value="person"
                        :name="bag.name"
                        type="radio"
                        @change="selectProduct"
                      >
                      <label
                        :key="'label-' + bag.name + '-hero-' + person"
                        :for="bag.name + '-hero-' + person"
                      >{{ person }}</label>
                    </template>
                  </p>
                </div>
                <div class="bag-hero__order-options-items--days">
                  <p
                    v-if="days"
                    class="bag-hero__order-options-label"
                  >
                    Dagar
                  </p>
                  <p
                    v-if="days"
                    class="bag-hero__radio-wrapper"
                  >
                    <template
                      v-for="day in days"
                    >
                      <input
                        :id="bag.name + '-day-target-' + day"
                        :key="'day-' + day"
                        v-model="selectedDays"
                        :value="day"
                        :name="'day-target-' + bag.name"
                        :disabled="!isDaysAvailable(day)"
                        type="radio"
                        @change="selectProduct"
                      >
                      <label
                        :key="'day-label-' + day"
                        :for="bag.name + '-day-target-' + day"
                      >{{ day }}</label>
                    </template>
                  </p>
                </div>
              </div>
            </div>
            <div class="bag-hero__order-options">
              <grocery-bag-order-check-box
                :gluten-free="glutenFree"
                :lactose-free="lactoseFree"
                :products="products"
                :is-gluten-free-available="isGlutenFreeAvailable"
                :is-lactose-free-available="isLactoseFreeAvailable"
                :name="bag.name"
                @lactoseFreeChange="doChangeLactoseFree"
                @glutenFreeChange="doChangeGlutenFree"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="bag-hero__button-wrapper col-12">
        <button
          v-if="!store"
          class="button button--40 button-primary button--fullwidth"
          @click.prevent="showStoreSearchModal"
        >
          Välj butik
        </button>

        <a
          v-if="storeLink && !isAddOn"
          class="button button--40 button-primary button--fullwidth"
          :href="storeLink"
        >
          Beställ
        </a>

        <a
          v-if="storeLink"
          class="one-time-buy button button--40 button--secondary button--fullwidth"
          :href="storeLinkOneTime"
        >
          Engångsköp
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import StoreInfo from "@/vue/components/groceryBagComponents/StoreInfo.vue"
import GroceryBagOrderCheckBox from '@/vue/components/groceryBagComponents/groceryBagOrderCheckBox.vue'
import GroceryBagPriceSplash from '@/vue/components/groceryBagComponents/groceryBagPriceSplash'
import "#/Logos/ICA"
import "#/Logos/ICA-Nara"
import "#/Logos/ICA-Supermarket"
import "#/Logos/ICA-Kvantum"
import "#/Logos/ICA-Maxi-Stormarknad"

export default {
  components: {
    'store-info': StoreInfo,
    'grocery-bag-order-check-box': GroceryBagOrderCheckBox,
    'grocery-bag-price-splash': GroceryBagPriceSplash
  },
  props: {
    currentProduct: {
      type: Object,
      default: () => {}
    },
    bag: {
      type: Object,
      default: () => {}
    },
    color: {
      type: String,
      default: "red"
    },
  },
  data () {
    return {
      dimensions: {
        small: {
          width: 196
        },
        medium: {
          width: 480
        },
        large: {
          width: 552
        }
      },
      glutenFree: null,
      lactoseFree: null,
      selectedDays: null,
      selectedPersons: null,
      showModal: false,
      vegetarian: null,
    }
  },
  computed: {
    ...mapState([
      'store',
      'selectedDeliveryMethod'
    ]),
    aspectRatio () {
      return this.$root.isSmallViewport ? 100 : 0
    },
    currentPrice () {
      if (this.currentProduct === null) {
        return 0
      }

      if (!this.store && this.currentProduct.campaignPrice) {
        return this.currentProduct.campaignPrice
      }

      if (this.selectedDeliveryMethod === 'pickup'){
        if (this.currentProduct.offerPickup) {
          return this.currentProduct.pricePickup
        }
        return this.currentProduct.recommendedPricePickup
      }

      if (this.selectedDeliveryMethod === 'homeDelivery'){
        if (this.currentProduct.offerHomeDelivery) {
          return this.currentProduct.priceHomeDelivery
        }
        return this.currentProduct.recommendedPriceHomeDelivery
      }

      return this.currentProduct.recommendedPricePickup || this.currentProduct.recommendedPriceHomeDelivery
    },
    currentPriceRecommended () {
      if (this.currentProduct === null) {
        return 0
      }

      return this.selectedDeliveryMethod === "pickup" ?  this.currentProduct.recommendedPricePickup : this.currentProduct.recommendedPriceHomeDelivery
    },
    offer () {
      const curr = this.currentProduct
      if (!curr) { return false }

      return (!this.store && !!curr.campaignPrice) || !!curr.offerPickup
    },
    days () {
      const actualDays = []

      for(const prod of this.bag.products) {
        actualDays.push(prod.days)
      }

      return new Set(actualDays.sort())
    },
    isAddOn () {
      return this.currentProduct && this.currentProduct.isAddOn
    },
    peoples () {
      const actualPeoples = []

      for(const prod of this.bag.products) {
        actualPeoples.push(prod.peoples)
      }

      return new Set(actualPeoples.sort())
    },
    products () {
      return this.bag.products
    },
    storeLink () {
      return this.makeStoreLink()
    },
    storeLinkOneTime () {
      return this.makeStoreLink(true)
    },
    isGlutenFreeAvailable () {
      return this.products.some(
        p => p.peoples === this.selectedPersons
          && p.days === this.selectedDays
          && p.glutenFree)
    },
    isLactoseFreeAvailable () {
      return this.products.some(
        p => p.peoples === this.selectedPersons
          && p.days === this.selectedDays
          && p.lactoseFree)
    },
  },
  watch: {
    selectedPersons (val) {
      if (this.isDaysAvailable(this.selectedDays)) {
        return
      }

      // set selectedDays to the first item that has the current number of persons
      this.selectedDays = this.products.filter(p => p.peoples === val)[0].days
    }
  },
  mounted () {
    this.selectedDays = this.currentProduct.days
    this.selectedPersons = this.currentProduct.peoples
  },
  methods: {
    doChangeLactoseFree (value) {
      this.lactoseFree = value
      this.selectProduct()
    },
    doChangeGlutenFree (value) {
      this.glutenFree = value
      this.selectProduct()
    },
    checkout (url) {
      location.href = url
    },
    isDaysAvailable (days) {
      if (this.selectedPersons === null) {
        return true
      }

      let prods = this.products.filter(
        p => p.peoples === this.selectedPersons)

      if (this.glutenFree) {
        prods = prods.filter(p => p.glutenFree === !!this.glutenFree)
      }

      if (this.lactoseFree) {
        prods = prods.filter(p => p.lactoseFree === !!this.lactoseFree)
      }
      return prods.some(p => p.days === days)
    },
    makeStoreLink (oneTime) {
      if (!this.store) { return }
      if (!this.currentProduct) { return }

      const discountCode = this.selectedDeliveryMethod === 'pickup'
        ? this.currentProduct.offerPickup
        : this.currentProduct.offerHomeDelivery

      const params = {
        storeid: this.store.id,
        productid: this.currentProduct.id,
        deliverytype: this.selectedDeliveryMethod.toLowerCase()
      }

      if (discountCode) {
        params.discountcode = discountCode
      }

      if (oneTime) {
        params.purchasetype = 1
      }

      const query = Object.keys(params)
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
        .join('&')

      return `/handla-online/ica-matkassen/matkasse-checkout/?${query}`
    },
    showStoreSearchModal () {
      this.$emit('showModal')
    },
    translateStoreType (storeType) {
      switch(storeType) {
        case 0:
          return 'ICA'
        case 1:
          return 'ICA-Nara'
        case 2:
          return 'ICA-Supermarket'
        case 3:
          return 'ICA-Kvantum'
        case 4:
          return 'ICA-Maxi-Stormarknad'
        default:
          return ''
      }
    },
    selectProduct () {
       this.$nextTick(() => {
        let prods = this.bag.products.filter(
          (p) => {
            return p.days === this.selectedDays && p.peoples === this.selectedPersons})

        if (this.glutenFree !== null) {
          prods = prods.filter(
            p => p.glutenFree === !!this.glutenFree)
        }

        if (this.lactoseFree !== null) {
          prods = prods.filter(
            p => p.lactoseFree === !!this.lactoseFree)
        }
        if (prods.length === 0) {
          return null
        }
        this.$emit('productChanged', prods[0])
      })
    },
  }
}
</script>

<style lang="scss">
.bag-hero {
  max-width: 38rem;
  margin: 0 auto;
  border: 1px solid $color-grey-special;
  border-radius: 2rem;
  padding: 1rem;
  position: relative;

  hr {
    border-top: 1px solid $color-grey-special;
  }

  .bag-hero__bag-name {
    @include font(rubrik, 3, "black");

    margin-top: 2.25rem;
    text-align: center;
  }

  .bag-hero__center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bag-hero__logo-container {
    border: 1px solid $color-grey-special;
    border-radius: 50%;
    box-shadow: $box-shadow-primary;
    height: 5.5rem;
    width: 5.5rem;
    position: absolute;
    top: -2.75rem;
    left: calc(50% - 2.75rem);
    background-color: #fff;

    @extend .bag-hero__center;

    .bag-hero__logo {
      fill: $color-primary-cta;
    }
  }

  .bag-hero__store-info {
    display: none;

    @include media-breakpoint-up(md) {
      display: block;
    }
  }

  .bag-hero__bag-info {
    padding-top: 1.5rem;
    border-top: 1px solid $color-grey-special;
    display: flex;
    justify-content: center;

    .bag-hero__bag-info--column {
      padding: 0 1rem;
      flex-grow: 0;
    }
  }

  &__order-options {
    color: $color-brown-dark;
    display: flex;
    padding: 1rem 0;

    &-items {
      display: flex;
      flex-direction: column;

      @include media-breakpoint-up(lg) {
        flex-flow: row wrap;
      }

      &--peoples,
      &--days:first-child {
        margin-right: 1.5rem;
      }

      &--lactose,
      &--gluten,
      &--vegetarian,
      &--discount {
        align-self: flex-end;
        margin: 0 1rem 0.4rem 0;
      }

      &--discount {
        margin-bottom: 1.3rem;
        display: flex;
      }
    }

    &-label {
      font-weight: 900;
      font-size: 14px;
      margin: 0 0 0.5rem;
      text-transform: uppercase;
    }
  }

  &__radio-wrapper {
    display: flex;
    position: relative;
    margin: 0 0 1rem;

    @include media-breakpoint-up(lg) {
      margin-bottom: 0;
    }

    input[type="radio"] {
      opacity: 0;
      position: absolute;
      margin: 0;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
    }

    label {
      padding: 0.25rem 1rem;
      border-top: 1px solid $color-grey-light;
      border-left: 1px solid $color-grey-light;
      border-bottom: 1px solid $color-grey-light;
      background-color: $color-white;

      @include font(text, 3 "black");

      &:first-of-type {
        border-radius: 0.25rem 0 0 0.25rem;
      }

      &:last-of-type {
        border-right: 1px solid $color-grey-light;
        border-radius: 0 0.25rem 0.25rem 0;
        margin-left: -1px;
      }

      &:only-of-type {
        border-radius: 0.25rem;
      }
    }

    [type="radio"]:checked + label {
      background: $color-red;
      color: $color-white;
      font-weight: 600;
    }

    [type="radio"]:disabled + label {
      background: $color-white;
      color: $color-grey-mid;
      cursor: not-allowed;
    }
  }

  &__price-container {
    @extend .bag-hero__center;
  }

  &__hero-price-container {
    display: flex;
    justify-content: center;
    margin-top: 1rem;

    @include media-breakpoint-up(lg) {
      align-items: center;
    }
  }

  &__button-wrapper a:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}
</style>
